import React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { DateTime, Duration } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';

// @mui/icons-material
import Phone from '@mui/icons-material/Phone';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardIcon from '../md/components/Card/CardIcon.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import newProspectPageStyle from '../assets/jss/crm/views/newProspectPageStyle';

import { clearTopReferrersInfo } from '../store/slices/dashboardStatsSlice';
import { clearReferringContact, addEmergencyContact, removeEmergencyContact } from '../store/slices/contactInfoSlice';
import { updateFirm } from '../store/slices/firmsSlice';
import { addClientToClientsInfo } from '../store/slices/clientsInfoSlice';
import { clearEmergencyContacts } from '../store/slices/emergencyContactsSlice';
import { clearInitialCallFormPrimingInfo, setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { removeNoteOnInquiry } from '../store/slices/notesSlice';
import { updateDocument } from '../store/slices/documentsSlice';
import { removeInquiry } from '../store/slices/inquiriesSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { addEmrClient } from '../store/slices/emrInfoSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumberAndRoles,
  validateEmail,
  isNonLocalNonLoggedInVisitor,
  sortedDropdownItemLabels,
  validateAddress,
  showAlert,
  constructPhoneRolesString,
  defaultBirthdayKludge,
  isFirmReferrerType,
  isProprietorshipType,
  isPractitionerType,
  convertToUserStdPhoneNumber,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import CrmTextField from '../components/CrmTextField';
import CrmButton from '../components/CrmButton';
import PhoneInput from '../components/PhoneInput';
import KSelect from '../components/KSelect';
import Showable from '../components/Showable';
import StateSelector from '../components/StateSelector';
import VobModal from '../components/VobModal';
import AlertModal from '../components/AlertModal';
import KDatePicker from '../components/KDatePicker';
import { searchableContactDataBuilder } from '../components/SearchableContactHierarchy';
import ReferralSelectionHierarchy from '../components/ReferralSelectionHierarchy';
import IcfConsultancyModal from '../components/IcfConsultancyModal';
import IcfReferrerFirmModal from '../components/IcfReferrerFirmModal';
import IcfPractitionerModal from '../components/IcfPractitionerModal';
import IcfOrgContactModal from '../components/IcfOrgContactModal';
import IcfUnaffiliatedContactModal from '../components/IcfUnaffiliatedContactModal';
import IcfPersonalReferrerModal from '../components/IcfPersonalReferrerModal';
import IcfEmergencyContactModal from '../components/IcfEmergencyContactModal';
import EmrFacilitiesDropdown from '../components/sync/EmrFacilitiesDropdown.jsx';

const localStyles = (theme) => ({
  ...newProspectPageStyle(theme),
  textFieldHolder: {
    margin: '12px 0',
    width: 'inherit',
  },
  horizDivider: {
    border: 0,
    borderTop: '1px solid #ddd',
    marginTop: '16px',
  },
});

class NewProspectPage extends React.Component {
  submissionOperationsQueue = [];
  referralId = 0;

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleProspectPhoneRoles = ['Mobile', 'Home', 'Work'];

  validationPolicy = {
    requireFirm: true,
    requireFirstContactDate: false,
    supplyDefaultFirstContactDate: true,
    requireProspectFirstName: true,
    requireProspectLastName: true,
    requireProspectSex: false,
    requireProspectAddress: false,
    forbidPartialProspectAddress: true,
    allowZipFreeProspectAddress: true,
    requireFirmAddress: false,
    forbidPartialFirmAddress: true,
    allowZipFreeFirmAddress: true,
    requireCommType: true,
    requireReferrerType: true,
    requireReferringFirmIfFirmTypeChosen: true,
    requireFirmContactIfFirmTypeChosen: true,
    requireReferringPersonIfRelatedTypeChosen: true,
    requireEmergencyContactIfSelfReferringChosen: false,
    requireEmergencyContactOrPhoneOrEmailIfSelfReferringChosen: true,
    requireProspectPhone: false,
    requireProspectEmail: false,
    requireProspectPhoneOrEmailIfNoEmergencyContact: true,
    requireEmergencyContactPhoneIfNoProspectPhone: false,
    requireEmergencyContactPhoneIfNoProspectPhoneOrEmail: true,
    requireEmergencyContactPhoneOrEmailIfNoProspectPhone: false,
    requireEmergencyContactPhoneOrEmailIfNoProspectPhoneOrEmail: true,
    requirefacilityNameIfSyncable: true,
  };

  blankPageState = {
    shouldShowVobForm: false,
    vobFormShouldBeBlank: true,
    errorMessage: '',
    redirect: undefined,
    prospectId: 0,
    prospectFirstName: '',
    prospectLastName: '',
    prospectBirthDate: null,
    prospectGender: '',
    prospectEmail: '',
    prospectPhoneRoles1: '',
    prospectPhoneNumber1: '',
    prospectPhoneRoles2: '',
    prospectPhoneNumber2: '',
    prospectAddressLine1: '',
    prospectAddressLine2: '',
    prospectCity: '',
    prospectState: '',
    prospectZip: '',
    prospectCountry: 'USA',
    expectedAdmissionDate: DateTime.local().startOf('day'),
    firstContactDate: DateTime.local().startOf('day'),
    chosenReferralCategory: 'Company / Organization',
    referrerType: '',
    referrerOrgId: 0,
    referrerOrgWebsite: null,
    referrerPersonId: 0,
    relationship: '',
    acquaintanceship: '',
    referralContactId: 0,
    chosenWordOfMouthOption: '',
    chosenSocialMediaOption: '',
    chosenMediaOption: '',
    referralOptionalSpecies: '',
    salesStage: '',
    campaign: '',
    communicationType: '',
    facility: '',
    intakeBy: '',
    primaryClinician: '',
    leadTemperature: 'Hot',
    contactName: '',
    contactPhone1Number: '',
    contactPhone2Number: '',
    contactEmail: '',
    contactPhone1Roles: '',
    contactPhone2Roles: '',
    prospectOwnerId: this.props.userInfo.personId,
    note: '',
    facilityName: '',
    configureToSyncWithEmr: false,
    disableSubmitButton: false,

    shouldShowReferrerFirmModalStateVar: false,
    shouldShowProprietorshipModalStateVar: false,
    shouldShowPractitionerModalStateVar: false,
    shouldShowOrgContactModalStateVar: false,
    shouldShowUnaffilContactModalStateVar: false,
    shouldShowPersonalReferrerModalStateVar: false,
    shouldAddEmergencyContact: false,
  };

  constructor(props) {
    super(props);

    this.state = { ...this.blankPageState };

    this.wordOfMouthOptions = sortedDropdownItemLabels('Word of Mouth Options');
    if (this.wordOfMouthOptions.length === 0) {
      this.wordOfMouthOptions = ['Family', 'Friends', 'Staff', 'Clients/Alumni', 'Contacts of Clients/Alumni', 'Other'];
    }

    this.socialMediaOptions = sortedDropdownItemLabels('Social Media Options');
    if (this.socialMediaOptions.length === 0) {
      this.socialMediaOptions = ['Facebook', 'Twitter', 'Instagram', 'TikTok', 'Other'];
    }

    this.mediaOptions = sortedDropdownItemLabels('Media Options');
    if (this.mediaOptions.length === 0) {
      this.mediaOptions = ['TV', 'Radio', 'Print', 'Internet', 'Other'];
    }
  }

  searchableContactData = searchableContactDataBuilder.build();

  assumeAlreadyFetchedBigDescrip = true;

  componentDidMount() {
    const continuation = () => {
      this.props.clearInitialCallFormPrimingInfo();
      this.fetchFirmInformationAndRender(true);
      this.setDropdownDefaults();
      if (this.state.shouldAddEmergencyContact) {
        this.displayEmergencyContact();
      } else {
        this.props.removeEmergencyContact(0);
      }
    };

    this.incorporatePrimingInfo(continuation);
  }

  incorporatePrimingInfo(continuation) {
    if (!this.props.primingInfo) {
      this.setState({ primingInquiryId: 0, callRailCallId: 0 });
      continuation();
      return;
    }

    // have primingInfo, but it might be expired
    const { date, inquiryId, callRailCallId, disposition } = this.props.primingInfo;
    const secondsSinceConversionRq = DateTime.local().diff(date).as('seconds');
    console.log('secondsSinceConversionRq: ' + secondsSinceConversionRq);
    if (secondsSinceConversionRq > 7) {
      this.setState({ primingInquiryId: 0 });
      continuation();
      return;
    }

    if (inquiryId > 0) {
      // have non-expired priming Info, so use it.
      const inquiry = DA.getInquiryById(inquiryId);
      const note = this.createNoteText(disposition, inquiry);
      const communicationType = this.mapInquiryCommunicationType(inquiry.communicationType);

      const sharedState = {
        primingInquiryId: inquiry.inquiryId,
        firstContactDate: inquiry.date || DateTime.local().startOf('day'),
        note,
        chosenReferralCategory: inquiry.referralSourceCategory,
        referrerType: inquiry.referralOrgType || inquiry.referrerType,
        companyId: inquiry.referralOrgId,
        referralContactId: inquiry.referralContactId,
        chosenWordOfMouthOption: inquiry.referralWordOfMouthType,
        chosenSocialMediaOption: inquiry.referralSocialMediaType,
        chosenMediaOption: inquiry.referralMediaType,
        referralOptionalSpecies: inquiry.referralSourceSpecies,
        campaign: inquiry.campaignId,
        communicationType,
        callRailCallId: inquiry.callRailCallId,
      };

      if (disposition === 'prospect') {
        this.setState(
          {
            ...sharedState,
            prospectFirstName: inquiry.firstName,
            prospectLastName: inquiry.lastName,
            prospectPhoneRoles1: inquiry.phone ? 'Mobile' : '',
            prospectPhoneNumber1: inquiry.phone,
            prospectEmail: inquiry.email,
          },
          continuation
        );
      } else {
        // disposition === "emergencyContact"
        this.setState(
          {
            ...sharedState,
            contactName: inquiry.firstName + ' ' + (inquiry.lastName || ''),
            contactPhone1Roles: 'Home',
            contactPhone1: inquiry.phone,
            contactPhone2: undefined,
            contactEmail: inquiry.email,
            referrerType: '',
            shouldAddEmergencyContact: true,
          },
          continuation
        );
      }
    } else if (callRailCallId > 0) {
      const callRailCall = DA.getCallRailCall(callRailCallId);

      const note = DA.getNoteAboutCallRailCallWithId(callRailCallId);
      const noteText = note ? note.content : '';
      const phoneNumber = convertToUserStdPhoneNumber(callRailCall.otherNumber);

      this.setState(
        {
          primingInquiryId: inquiryId,
          firstContactDate: callRailCall.date || DateTime.local().startOf('day'),
          prospectPhoneRoles1: phoneNumber ? 'Mobile' : '',
          prospectPhoneNumber1: phoneNumber,
          chosenReferralCategory: 'Company / Organization',
          referrerType: '',
          companyId: 0,
          referralContactId: 0,
          note: noteText ? noteText : '',
          callRailCallId,
        },
        continuation
      );
    }
  }

  mapInquiryCommunicationType(inquiryCommType) {
    if (inquiryCommType === 'ApiCall' || inquiryCommType === 'CallRail Form WebHook') {
      return 'Web Form';
    } else {
      return inquiryCommType;
    }
  }

  createNoteText(conversionType, inquiry) {
    const msg = inquiry.message;
    if (inquiry.source === 'CrmForm') {
      return msg;
    }
    const name = (inquiry.firstName + ' ' + (inquiry.lastName || '')).trim();
    if (conversionType === 'prospect') {
      return `Contacted by prospect, leaving message: "${msg}"`;
    } else if (conversionType === 'emergencyContact') {
      return `Contacted by emergency contact, ${name}, leaving message: "${msg}"`;
    } /* else if (conversionType === 'referralContact') {
      return `Contacted by referral contact, ${name}, leaving message: "${msg}"`;
    } */ else {
      return '';
    }
  }

  fetchFirmInformationAndRender() {
    this.setState({ errorMessage: '' });
    let firmId = this.props.firmToShow;
    if (!firmId || firmId < 1) {
      firmId = DA.getFirmId();
    }
    if (firmId && firmId > 0) {
      this.preselectSingleItemLists();
    }

    // if "syncedEmr" is not an empty string, it means sync is enabled
    this.setState({ configureToSyncWithEmr: this.props.emrInfo.isSyncedWithFirm });
  }

  setDropdownDefaults() {
    const settingName = 'dropdownSelectionDefaults';
    const defaultsSettings = DA.getUserSetting(settingName); // an array of objects, one for each page with defaultable dropdowns
    if (defaultsSettings.length === 0) {
      return;
    }
    const structForThisPage = defaultsSettings.find((s) => s.pageCode === 'newProspectPage'); // an object {pageCode: 'newProspectPage', defaults: [...]}
    if (!structForThisPage) {
      return;
    }
    const defaults = structForThisPage.defaults; // and array like [{ddName: 'Communication Type', defaultValue: 'Phone Call'}, {...}, {...}]
    const commTypeObj = defaults.find((obj) => obj.ddName === 'Communication Type'); // an object like {ddName: 'Communication Type', defaultValue: 'Phone Call'}
    if (commTypeObj) {
      const commTypeDefault = commTypeObj.defaultValue; // a string like 'Phone Call'
      this.setState({ communicationType: commTypeDefault });
    }
    const refTypeObj = defaults.find((obj) => obj.ddName === 'Referral Type'); // an object like {ddName: 'Referral Type', defaultValue: 'Social Media'}
    if (refTypeObj) {
      const refTypeDefault = refTypeObj.defaultValue; // a string like 'Social Media'
      this.setState({ chosenReferralCategory: refTypeDefault });
    }
  }

  handleStateChange(newValue) {
    this.setState({ prospectState: newValue });
  }

  handleDateOfBirthChange(newDate) {
    this.setState({ prospectBirthDate: newDate });
  }

  handleAdmissionDateChange(newDate) {
    this.setState({ expectedAdmissionDate: newDate });
  }

  handleFirstContactDateChange(newDate) {
    this.setState({ firstContactDate: newDate });
  }

  handleSelectChange(event) {
    this.setState({ [event.target.name]: event.target.value }, () => this.maybeAutoSelectAfterEvent(event));
  }

  maybeAutoSelectAfterEvent(event) {
    if (event.target.name === 'referrerOrgId') {
      const selectedOrg = DA.getFirmById(this.state.referrerOrgId);
      const nContactsAtSelectedOrg = selectedOrg && selectedOrg.contactIds ? selectedOrg.contactIds.length : 0;
      if (nContactsAtSelectedOrg === 1) {
        this.setState({ referralContactId: selectedOrg.contactIds[0] });
      }
    }
  }

  handleReferrerTypeChange(newValue) {
    this.setState(
      {
        referrerType: newValue,
        referrerOrgId: 0,
        relationship: '',
        acquaintanceship: '',
        referrerPersonId: 0,
        referralContactId: 0,
      },
      () => this.preselectSingleItemLists()
    );
  }

  handleProspectPhoneRoles1Change(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ prospectPhoneRoles1: newPhoneRoles });
  }

  handleProspectPhoneNumber1Change(newNumber) {
    this.setState({ prospectPhoneNumber1: newNumber });
  }

  handleProspectPhoneRoles2Change(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ prospectPhoneRoles2: newPhoneRoles });
  }

  handleProspectPhoneNumber2Change(newNumber) {
    this.setState({ prospectPhoneNumber2: newNumber });
  }

  handleAddEmergencyContactClick() {
    this.setState({ shouldAddEmergencyContact: true });
  }

  handleSubmit() {
    const validation = this.validateEntireForm();
    if (validation.valid) {
      this.setState({ disableSubmitButton: true });
      this.submit();
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateEntireForm() {
    const referralInfoValidation = this.validateReferralInfo();
    if (!referralInfoValidation.valid) {
      return referralInfoValidation;
    }

    if (this.validationPolicy.requireFirm) {
      if (!DA.getFirmId()) {
        return {
          valid: false,
          errorMessage: 'You must be logged in as a User of some Provider',
        };
      }
    }

    if (this.state.prospectEmail) {
      const emailValidation = validateEmail(this.state.prospectEmail, 'Prospect Email seems invalid');
      if (!emailValidation.valid) {
        return emailValidation;
      }
    }

    const { prospectPhoneNumber1, prospectPhoneRoles1 } = this.state;

    if (prospectPhoneNumber1) {
      const phoneValidation = validatePhoneNumberAndRoles(
        prospectPhoneNumber1,
        prospectPhoneRoles1,
        'Prospect Phone Number'
      );
      if (!phoneValidation.valid) {
        return phoneValidation;
      }
    }

    if (this.validationPolicy.requireFirstContactDate) {
      if (!this.state.firstContactDate) {
        return {
          valid: false,
          errorMessage: 'You must specify a First Contact Date',
        };
      }
    }

    if (this.validationPolicy.supplyDefaultFirstContactDate) {
      if (!this.state.firstContactDate) {
        this.setState({ firstContactDate: DateTime.local().startOf('day') });
      }
    }

    if (this.validationPolicy.requireProspectFirstName) {
      if (!this.state.prospectFirstName) {
        return {
          valid: false,
          errorMessage: 'You must supply a Prospect First Name',
        };
      }
    }

    if (this.validationPolicy.requireProspectLastName) {
      if (!this.state.prospectLastName) {
        return {
          valid: false,
          errorMessage: 'You must supply a Prospect Last Name',
        };
      }
    }

    if (this.validationPolicy.requireProspectSex) {
      if (!this.state.prospectGender) {
        return {
          valid: false,
          errorMessage: "Please specify this Prospect's Gender",
        };
      }
    }

    if (this.validationPolicy.requireSalesStage) {
      if (!this.state.salesStage) {
        return {
          valid: false,
          errorMessage: 'You must choose a Sales Stage',
        };
      }
    }

    if (this.validationPolicy.requireCommType) {
      if (!this.state.communicationType) {
        return {
          valid: false,
          errorMessage: 'You must choose a Communication Type',
        };
      }
    }

    const { requireProspectAddress, forbidPartialProspectAddress, allowZipFreeProspectAddress } = this.validationPolicy;

    if (requireProspectAddress || forbidPartialProspectAddress || allowZipFreeProspectAddress) {
      const { prospectAddressLine1, prospectAddressLine2, prospectCity, prospectState, prospectZip } = this.state;

      const addressValidation = validateAddress(
        prospectAddressLine1,
        prospectAddressLine2,
        prospectCity,
        prospectState,
        prospectZip,
        requireProspectAddress,
        forbidPartialProspectAddress,
        allowZipFreeProspectAddress
      );
      if (!addressValidation.valid) {
        return addressValidation;
      }
    }

    if (this.validationPolicy.requireProspectPhone) {
      if (!prospectPhoneNumber1) {
        return {
          valid: false,
          errorMessage: 'You must supply a Phone Number for this prospect',
        };
      }
      const phoneValidation = validatePhoneNumberAndRoles(
        prospectPhoneNumber1,
        prospectPhoneRoles1,
        'Prospect Phone Number'
      );
      if (!phoneValidation.valid) {
        return phoneValidation;
      }
    }

    if (this.validationPolicy.requireProspectPhoneOrEmailIfNoEmergencyContact) {
      if (this.props.contactInfo.emergencyContacts.length === 0) {
        if (!this.state.prospectPhoneNumber1 && !this.state.prospectEmail) {
          return {
            valid: false,
            errorMessage:
              "If you don't Add a Prospect Contact, you must supply a Phone Number or Email for this prospect",
          };
        }
      }
    }

    if (this.validationPolicy.requireEmergencyContactPhoneIfNoProspectPhoneOrEmail) {
      if (!this.state.prospectPhoneNumber1 && !this.state.prospectEmail) {
        if (this.props.contactInfo.emergencyContacts.length === 0) {
          return {
            valid: false,
            errorMessage:
              "If you can't supply a Phone Number or Email for this prospect, you must Add a Prospect Contact",
          };
        }
        if (!this.haveEmergencyContactWithPhoneNumber()) {
          const errorMessageP1 = "If you can't supply a Phone Number for this prospect,";
          const errorMessageP2 = '\nyou must Add a Prospect Contact with a phone number';
          const errorMessage = errorMessageP1 + errorMessageP2;
          return {
            valid: false,
            errorMessage,
          };
        }
      }
    }

    if (this.validationPolicy.requireEmergencyContactPhoneIfNoProspectPhone) {
      if (!this.state.prospectPhoneNumber1) {
        if (this.props.contactInfo.emergencyContacts.length === 0) {
          return {
            valid: false,
            errorMessage: "If you can't supply a Phone Number for this prospect, you must Add a Prospect Contact",
          };
        }
        if (!this.haveEmergencyContactWithPhoneNumber()) {
          const errorMessageP1 = "If you can't supply a Phone Number for this prospect,";
          const errorMessageP2 = '\nyou must Add a Prospect Contact with a phone number';
          const errorMessage = errorMessageP1 + errorMessageP2;
          return {
            valid: false,
            errorMessage,
          };
        }
      }
    }

    if (this.validationPolicy.requireEmergencyContactPhoneOrEmailIfNoProspectPhone) {
      if (!this.state.prospectPhoneNumber1) {
        if (this.props.contactInfo.emergencyContacts.length === 0) {
          return {
            valid: false,
            errorMessage: "If you can't supply a Phone Number for this prospect, you must Add a Prospect Contact",
          };
        }
        if (!this.haveEmergencyContactWithPhoneNumber() && !this.haveEmergencyContactWithEmail()) {
          const errorMessageP1 = "If you can't supply a Phone Number for this prospect,";
          const errorMessageP2 = '\nyou must Add a Prospect Contact with a phone number or email';
          const errorMessage = errorMessageP1 + errorMessageP2;
          return {
            valid: false,
            errorMessage,
          };
        }
      }
    }

    if (this.validationPolicy.requireEmergencyContactPhoneOrEmailIfNoProspectPhoneOrEmail) {
      if (!this.state.prospectPhoneNumber1 && !this.state.prospectEmail) {
        if (this.props.contactInfo.emergencyContacts.length === 0) {
          return {
            valid: false,
            errorMessage:
              "If you can't supply a Phone Number or Email for this prospect, you must Add a Prospect Contact",
          };
        }
        if (!this.haveEmergencyContactWithPhoneNumber() && !this.haveEmergencyContactWithEmail()) {
          const errorMessageP1 = "If you can't supply a Phone Number or Email for this prospect,";
          const errorMessageP2 = '\nyou must Add a Prospect Contact with a phone number or email';
          const errorMessage = errorMessageP1 + errorMessageP2;
          return {
            valid: false,
            errorMessage,
          };
        }
      }
    }

    const { prospectPhoneNumber2, prospectPhoneRoles2 } = this.state;

    const phone1Validation = validatePhoneNumberAndRoles(prospectPhoneNumber1, prospectPhoneRoles1, 'Phone 1');
    if (!phone1Validation.valid) {
      return phone1Validation;
    }

    const phone2Validation = validatePhoneNumberAndRoles(prospectPhoneNumber2, prospectPhoneRoles2, 'Phone 2');
    if (!phone2Validation.valid) {
      return phone2Validation;
    }

    if (this.validationPolicy.requirefacilityNameIfSyncable && this.state.configureToSyncWithEmr) {
      if (!this.state.facilityName) {
        return {
          valid: false,
          errorMessage: 'You must select a Facility for this Prospect.',
        };
      }
    }

    // We passed all the tests, so return a "valid" response
    return {
      valid: true,
      errorMessage: '',
    };
  }

  validateReferralInfo() {
    const { chosenReferralCategory } = this.state;

    if (this.state.referralContactId) {
      // just assume that we got this way legitimately,
      //  either through specifying an unaffiliated contact
      //  or by picking a known one that resulted in filling in the rest

      return { valid: true };
    }

    if (chosenReferralCategory === 'Company / Organization') {
      if (!this.state.referrerType) {
        return {
          valid: false,
          errorMessage: 'You must choose a Referral Organization Type',
        };
      }
      if (!this.state.referrerOrgId) {
        return {
          valid: false,
          errorMessage: 'You must specify a Referring ' + this.state.referrerType,
        };
      }
      if (!this.state.referralContactId) {
        return {
          valid: false,
          errorMessage: 'You must specify a Contact as the Referring ' + this.state.referrerType,
        };
      }
    } else if (chosenReferralCategory === 'Word of Mouth') {
      if (!this.state.chosenWordOfMouthOption) {
        return {
          valid: false,
          errorMessage: 'You must specify a Word of Mouth Source',
        };
      }
    } else if (chosenReferralCategory === 'Social Media') {
      if (!this.state.chosenSocialMediaOption) {
        return {
          valid: false,
          errorMessage: 'You must choose a particular Medium',
        };
      }
    } else if (chosenReferralCategory === 'Media') {
      if (!this.state.chosenMediaOption) {
        return {
          valid: false,
          errorMessage: 'You must choose a particular Medium',
        };
      }
    } else if (chosenReferralCategory === 'Marketing Event/Campaign') {
      if (!this.state.campaign) {
        return {
          valid: false,
          errorMessage: 'Having chosen the Campaign Referral Type, you must choose a particular Campaign',
        };
      }
    }

    if (chosenReferralCategory !== 'Company / Organization') {
      if (
        !this.state.referrerPersonId &&
        this.props.contactInfo.emergencyContacts.length === 0 &&
        !this.state.prospectEmail &&
        !this.state.prospectPhoneNumber1
      ) {
        return {
          valid: false,
          errorMessage: 'You must add a Contact or a Phone number or an Email for this prospect',
        };
      }
    }

    return { valid: true };
  }

  haveEmergencyContactWithPhoneNumber() {
    return this.props.contactInfo.emergencyContacts.some((contact) => contact.phoneNumbers.length > 0);
  }

  haveEmergencyContactWithEmail() {
    return this.props.contactInfo.emergencyContacts.some((contact) => contact.email);
  }

  countTrueValues(obj) {
    return Object.values(obj).filter((v) => v).length;
  }

  // return { id, name } pair for each active contact
  getActiveContactsForSelectedOrg() {
    if (this.state.referrerOrgId <= 0) {
      return [{ id: 0, name: 'Unknown' }];
    }
    const selectedFirm = DA.getFirmById(this.state.referrerOrgId);
    const contactIds = selectedFirm ? selectedFirm.contactIds : [];
    if (contactIds.length === 0) {
      return [{ id: 0, name: 'Unknown' }];
    }
    const activeIds = contactIds.filter((id) => this.contactIdIsActive(id));
    const idAndNamePairs = activeIds.map((id) => {
      const person = DA.getPersonById(id);
      const name = person.preferredFullName;
      return { id, name };
    });
    return idAndNamePairs;
  }

  contactIdIsActive(contactId) {
    const contact = DA.getPersonById(contactId);
    return contact && !contact.inactive;
  }

  shouldShowAddEmergencyContactButton() {
    // TODO add more to this, to handle when referrerType in (relative, acqaintance)
    //  and emergencyContacts list is empty
    // const { referrerType, referrerPersonId, referralContactId } = this.state;
    // return referrerPersonId || referralContactId || referrerType === 'Self';

    // return this.state.chosenReferralCategory !== 'Company / Organization';

    return true;
  }

  getKnownFirmsOfReferrerType() {
    const { knownFirms } = this.props.firmInfo;
    return knownFirms.filter((firm) => firm.firmType === this.state.referrerType);
  }

  getKnownPractices() {
    const { knownFirms } = this.props.firmInfo;
    return knownFirms.filter((firm) => firm.firmType === 'Private Practitioner');
  }

  preselectSingleItemLists() {
    const { facilities, /* campaigns,*/ intakers, clinicians } = this.props.firmInfo;
    if (facilities && facilities.length === 1) {
      const uniqueFacilityId = facilities[0].id;
      if (!this.state.facility || this.state.facility === '0') {
        this.setState({ facility: uniqueFacilityId });
      }
    }

    /*
            if (campaigns && campaigns.length === 1) {
                const uniqueCampaignId = campaigns[0].id;
                if (this.state.campaign === 0 || this.state.campaign === "0") {
                    this.setState({campaign: uniqueCampaignId});
                }
            }
    */

    if (intakers && intakers.length === 1) {
      const uniqueIntakerId = intakers[0].id;
      if (!this.state.intakeBy || this.state.intakeBy === '0') {
        this.setState({ intakeBy: uniqueIntakerId });
      }
    }

    if (clinicians && clinicians.length === 1) {
      const uniqueClinicianId = clinicians[0].id;
      if (!this.state.primaryClinician || this.state.primaryClinician === '0') {
        this.setState({ primaryClinician: uniqueClinicianId });
      }
    }

    if (isFirmReferrerType(this.state.referrerType)) {
      const knownFirmsOfReferrerType = this.getKnownFirmsOfReferrerType();
      if (knownFirmsOfReferrerType.length === 1) {
        const uniqueFirm = knownFirmsOfReferrerType[0];
        const uniqueId = uniqueFirm.id;
        if (!this.state.referrerOrgId) {
          this.setState({ referrerOrgId: uniqueId });
          const contactIds = DA.getFirmById(uniqueId).contactIds;
          if (contactIds.length === 1) {
            this.setState({ referralContactId: contactIds[0] });
          }
        }
      }
    }
  }

  shouldShowOrgContactModal() {
    return this.state.shouldShowOrgContactModalStateVar;
  }

  shouldShowUnaffilContactModal() {
    return this.state.shouldShowUnaffilContactModalStateVar;
  }

  shouldShowPersonalReferrerModal() {
    return this.state.shouldShowPersonalReferrerModalStateVar;
  }

  cancelContactModal() {
    this.setState({
      shouldShowOrgContactModalStateVar: false,
      shouldShowUnaffilContactModalStateVar: false,
      shouldShowPersonalReferrerModalStateVar: false,
      shouldAddEmergencyContact: false,
    });
  }

  shouldShowEmergencyContactModal() {
    return this.state.shouldAddEmergencyContact;
  }

  displayEmergencyContactFromValues(modalStateValues) {
    this.setState({ ...modalStateValues }, () => this.displayEmergencyContact(modalStateValues.relationship));
  }

  displayEmergencyContact(relationship) {
    const contact = {
      personId: 0,
      relationship: relationship || 'Unknown',
      name: this.state.contactName.trim(),
      email: this.state.contactEmail,
      phoneNumbers: this.buildContactPhoneNumbersList(),
    };
    this.cancelContactModal();
    this.props.addEmergencyContact(contact);
    this.setState({ shouldAddEmergencyContact: false });
  }

  shouldShowReferrerFirmModal() {
    return this.state.shouldShowReferrerFirmModalStateVar;
  }

  shouldShowProprietorshipModal() {
    return this.state.shouldShowProprietorshipModalStateVar;
  }

  cancelProprietorshipModal() {
    this.setState({ referrerType: '', shouldShowProprietorshipModalStateVar: false });
  }

  handleNewReferrerFirmSuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referrerType: visibleFirmType,
      referrerOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referrerType: visibleFirmType,
      referrerOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referrerType: visibleFirmType,
      referrerOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewOrgContactSuccess(contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({ referralContactId: contactId, shouldShowOrgContactModalStateVar: false });
  }

  handleNewUnaffilContactSuccess(contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({ referralContactId: contactId, shouldShowUnaffilContactModalStateVar: false });
  }

  cancelReferrerFirmModal() {
    if (isFirmReferrerType(this.state.referrerType)) {
      const knownFirmsOfReferrerType = this.getKnownFirmsOfReferrerType();
      if (knownFirmsOfReferrerType.length === 0) {
        this.setState({ referrerType: '' });
      }
      this.setState({ referrerOrgId: 0, shouldShowReferrerFirmModalStateVar: false });
    }
  }

  shouldShowPractitionerModal() {
    return this.state.shouldShowPractitionerModalStateVar;
  }

  cancelPractitionerModal() {
    const knownPractices = this.getKnownPractices();
    if (knownPractices.length === 0) {
      this.setState({ referrerType: '' });
    }
    this.setState({ referrerOrgId: 0, shouldShowPractitionerModalStateVar: false });
  }

  handleNewPersonalReferrer(contactId, relationship) {
    const { referrerType } = this.state;
    const contact = DA.getPersonById(contactId);
    const contactOption = {
      businessId: `Cat:${referrerType}:Name:${relationship}`,
      relationship: relationship,
      name: contact.preferredFullName,
      personId: contactId,
    };
    this.searchableContactData = searchableContactDataBuilder.build(contactOption);

    this.setState({
      referrerPersonId: contactId,
      referralContactId: contactId,
      shouldShowPersonalReferrerModalStateVar: false,
    });
  }

  validateAsSimplePersonName(name, errorMessage) {
    const valid = name && name.length >= 6 && name.indexOf(' ') > 1;
    return { valid, errorMessage };
  }

  buildContactPhoneNumbersList() {
    const { contactPhone1Roles, contactPhone1Number, contactPhone2Roles, contactPhone2Number } = this.state;
    const phoneNumbers = [];
    if (contactPhone1Number) {
      const phoneApiStruct = { roles: contactPhone1Roles, countryCode: 1, number: contactPhone1Number };
      phoneNumbers.push(phoneApiStruct);
    }
    if (contactPhone2Number) {
      const phoneApiStruct = { roles: contactPhone2Roles, countryCode: 1, number: contactPhone2Number };
      phoneNumbers.push(phoneApiStruct);
    }
    return phoneNumbers;
  }

  postsubmissionPolicy = { op: 'redirectToCasePage', arg: null };
  // postsubmissionPolicy = { op: "redirectToHomePage", arg: null};
  // postsubmissionPolicy = { op: "refreshThisPage", arg: null};

  submit() {
    // Create a Person record for prospect
    this.appendSubmissionOperation('createPersonRecordForProspect', {});

    this.appendSubmissionOperation('addEmrClientForProspect', {});

    // Create a Person record and a ClientContact record for any prospect contacts that don't yet have one
    this.props.contactInfo.emergencyContacts.forEach((contact) => {
      this.appendSubmissionOperation('createPersonAndClientContactRecordsForEmergencyContact', contact);
    });

    // Create a Referral record
    this.appendSubmissionOperation('createReferralRecord', {});

    // Create a Cаse record
    this.appendSubmissionOperation('createCaseRecord', {});

    if (this.state.note) {
      this.appendSubmissionOperation('createNoteRecord', {});
    }

    // Save the VOB Form Info, if any
    this.appendSubmissionOperation('saveVobFormInfo', {});

    // Transition from "None" to "Prospect" status
    this.appendSubmissionOperation('transitionToProspectStatus', {});

    // Clear the contactInfo state from Redux
    this.appendSubmissionOperation('clearReduxContactInfoState', {});

    // Would be nice to fire off some "program"
    this.appendSubmissionOperation('activateCasePrograms', {});

    // Convert any Notes on the Inquiry into Notes on the Person
    this.appendSubmissionOperation('convertInquiryNotes', {});

    // Convert any Documents on the Inquiry into Documents on the Person
    this.appendSubmissionOperation('convertInquiryDocuments', {});

    // If we built this case on an Inquiry, delete that inquiry
    this.appendSubmissionOperation('deleteInquiry', {});

    // Redirect to home or case page or something like that
    this.appendSubmissionOperation(this.postsubmissionPolicy.op, {});

    // Clear all the state on this form; save this until after redirection so we can use this.state.prospectId
    this.appendSubmissionOperation('clearPageState', {});

    this.processSubmissionOperationsQueue();
  }

  appendSubmissionOperation(operation, payload) {
    this.submissionOperationsQueue.push({ operation, payload });
  }

  processSubmissionOperationsQueue() {
    if (this.submissionOperationsQueue.length === 0) {
      return;
    }
    const currentOperationStruct = this.submissionOperationsQueue[0];
    this.submissionOperationsQueue = this.submissionOperationsQueue.slice(1);
    this.processSubmissionOperation(currentOperationStruct);
  }

  processSubmissionOperation(operationStruct) {
    const { operation, payload } = operationStruct;
    console.log(operation);
    switch (operation) {
      case 'createPersonRecordForProspect':
        this.createPersonRecordForProspect();
        break;
      case 'addEmrClientForProspect':
        this.addEmrClientForProspect();
        break;
      case 'createPersonAndClientContactRecordsForEmergencyContact':
        this.createPersonAndClientContactRecordsForEmergencyContact(payload);
        break;
      case 'createReferralRecord':
        this.createReferralRecord();
        break;
      case 'createCaseRecord':
        this.createCaseRecord();
        break;
      case 'createNoteRecord':
        this.createNoteRecord();
        break;
      case 'saveVobFormInfo':
        this.saveVobFormInfo();
        break;
      case 'transitionToProspectStatus':
        this.transitionToProspectStatus();
        break;
      case 'convertInquiryNotes':
        this.convertInquiryNotes();
        break;
      case 'convertInquiryDocuments':
        this.convertInquiryDocuments();
        break;
      case 'deleteInquiry':
        this.deleteInquiry();
        break;
      case 'clearPageState':
        this.clearPageState();
        break;
      case 'clearReduxContactInfoState':
        this.clearReduxContactInfoState();
        break;
      case 'activateCasePrograms':
        this.activateCasePrograms();
        break;
      case 'redirectToCasePage':
        this.redirectToCasePageAfterSubmission();
        break;
      case 'redirectToHomePage':
        this.redirectHomeAfterSubmission();
        break;
      case 'refreshThisPage':
        this.refreshThisPage();
        break;
      case 'preselectSingleItemLists':
        this.preselectSingleItemLists();
        break;
      default:
        showAlert(this, 'unknown submission operation: ' + operation);
    }
  }

  createPersonRecordForProspect() {
    const uri = '/api/person';
    const personApiStruct = this.buildPersonApiStructForProspect();
    const request = createStdRequest(uri, 'post', personApiStruct);
    const actionOnGoodResponse = (json) => {
      const prospectId = json.personId;
      this.setState({ prospectId });
      this.props.addClientToClientsInfo(prospectId);
      this.processSubmissionOperationsQueue();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  addEmrClientForProspect() {
    if (this.props.emrInfo.isSyncedWithFirm) {
      const newEmrClient = {
        personId: this.state.prospectId,
        name: {
          first: this.state.prospectFirstName.trim(),
          last: this.state.prospectLastName.trim(),
        },
        phone: {
          other: this.state.prospectPhoneNumber1,
        },
        facilityName: this.state.facilityName,
      };
      this.props.addEmrClient(newEmrClient);
    }
    this.processSubmissionOperationsQueue();
  }

  buildPersonApiStructForProspect() {
    const {
      prospectFirstName,
      prospectLastName,
      prospectEmail,
      prospectPhoneNumber1,
      prospectPhoneRoles1,
      prospectPhoneNumber2,
      prospectPhoneRoles2,
    } = this.state;
    const personId = 0;
    const email = prospectEmail;
    const firstName = prospectFirstName.trim();
    const lastName = prospectLastName.trim();
    const preferredName = firstName;
    const phoneNumbers = [];
    // check this
    if (prospectPhoneNumber1) {
      const phoneApiStruct = { roles: prospectPhoneRoles1, countryCode: 1, number: prospectPhoneNumber1 };
      phoneNumbers.push(phoneApiStruct);
    }
    if (prospectPhoneNumber2) {
      const phoneApiStruct = { roles: prospectPhoneRoles2, countryCode: 1, number: prospectPhoneNumber2 };
      phoneNumbers.push(phoneApiStruct);
    }
    let addresses = [];
    const {
      prospectAddressLine1,
      prospectAddressLine2,
      prospectCity,
      prospectState,
      prospectZip,
      prospectGender,
      prospectBirthDate,
    } = this.state;
    // question why do we only need to compare line one
    // must test!
    if (prospectAddressLine1) {
      const address = {
        roles: 'Home',
        isIntlStr: 'false',
        line1: prospectAddressLine1.trim(),
        line2: prospectAddressLine2.trim(),
        city: prospectCity.trim(),
        state: prospectState.trim(),
        zip: prospectZip,
      };
      addresses = [address];
    }
    const sex = prospectGender || 'Unknown';
    const bDayToUse = !prospectBirthDate || prospectBirthDate.year < 1900 ? defaultBirthdayKludge : prospectBirthDate;
    const birthDate = bDayToUse.toISO();
    return {
      personId,
      email,
      firstName,
      lastName,
      preferredName,
      sex,
      birthDate,
      phoneNumbers,
      addresses,
    };
  }

  createPersonAndClientContactRecordsForEmergencyContact(contact) {
    const personApiStruct = this.buildPersonApiStructForEmergencyContact(contact);
    const contactId = personApiStruct.personId;
    if (contactId === 0) {
      // this is contact of prospect not the company!!!
      const uri = '/api/person';
      const request = createStdRequest(uri, 'post', personApiStruct);
      const actionOnGoodResponse = (json) => {
        const newContactId = json.personId;
        this.createClientContactRecord(newContactId, contact.relationship);
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      this.createClientContactRecord(contactId, contact.relationship);
    }
  }

  buildPersonApiStructForEmergencyContact(contact) {
    const { personId, name, email, phoneNumbers } = contact;
    const trimmedName = name.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const middleNames = trimmedName.slice(firstSpaceLoc, lastSpaceLoc).trim();
    const lastName = trimmedName.substring(lastSpaceLoc + 1);
    const preferredName = firstName;
    const addresses = [];
    return {
      personId,
      email,
      firstName,
      middleNames,
      lastName,
      preferredName,
      phoneNumbers,
      addresses,
    };
  }

  createClientContactRecord(contactId, relationship) {
    const clientId = this.state.prospectId;
    const uri = '/api/person/' + clientId + '/contact';
    // for now, we won't initialize the contact's description on the initialCallPage
    const description = '';
    const viewingFirmId = DA.getFirmId();
    const apiStruct = { clientId, contactId, relationship, description, viewingFirmId };
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.processSubmissionOperationsQueue();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  createReferralRecord() {
    const uri = '/api/referral';
    const apiStruct = this.buildReferralApiStruct();
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.clearTopReferrersInfo();
      const referral = json.referral;
      ReduxLoader.addReferral(referral);
      this.referralId = referral.referralId;
      const visibleFirmId = apiStruct.referringOrgId;
      if (visibleFirmId !== 0) {
        const visibleFirm = DA.getFirmById(visibleFirmId);
        const existingReferralFromIds = visibleFirm.referralFromIds;
        const extendedIds = [...existingReferralFromIds, this.referralId];
        const augmentedVisibleFirm = { ...visibleFirm, referralFromIds: extendedIds };
        this.props.updateFirm(augmentedVisibleFirm);
      }
      this.processSubmissionOperationsQueue();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildReferralApiStruct() {
    const {
      firstContactDate,
      salesStage,
      communicationType,
      chosenReferralCategory,
      chosenWordOfMouthOption,
      chosenSocialMediaOption,
      chosenMediaOption,
      referrerType,
      referralOptionalSpecies,
      prospectId,
      referrerOrgId,
      campaign,
      callRailCallId,
    } = this.state;
    const referralId = 0;
    const contactDate = firstContactDate ? firstContactDate.toISO() : DateTime.local().startOf('day').toISO();
    const referreeId = prospectId;
    const referringPersonId = this.getReferringPersonId();
    const referringOrgId = referrerOrgId;
    const clientContactRelationship = this.computeRelationship();
    const receivingUserPersonId = this.props.userInfo.personId;
    const receivingOrgId = DA.getFirmId();
    const campaignId = campaign || 0;
    const substanceNameStarts = [];
    let sourceType = '';
    let sourceSpecies = referralOptionalSpecies;
    if (chosenReferralCategory === 'Company / Organization') {
      sourceType = referrerType;
    } else if (chosenReferralCategory === 'Word of Mouth') {
      sourceType = chosenWordOfMouthOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Word of Mouth';
        if (referralOptionalSpecies) {
          sourceSpecies = referralOptionalSpecies;
        }
      }
    } else if (chosenReferralCategory === 'Social Media') {
      sourceType = chosenSocialMediaOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Social Media';
        if (referralOptionalSpecies) {
          sourceSpecies = referralOptionalSpecies;
        }
      }
    } else if (chosenReferralCategory === 'Media') {
      sourceType = chosenMediaOption;
      if (sourceType === 'Other') {
        sourceType = 'Other Media';
        if (referralOptionalSpecies) {
          sourceSpecies = referralOptionalSpecies;
        }
      }
    } else {
      sourceType = chosenReferralCategory;
    }

    return {
      referralId,
      contactDate,
      communicationType,
      sourceType,
      sourceSpecies,
      referreeId,
      referringPersonId,
      referringOrgId,
      clientContactPersonId: 0, // don't add gratuitous contact
      clientContactRelationship,
      receivingUserPersonId,
      receivingOrgId,
      campaignId,
      salesStage,
      substanceNameStarts,
      callRailCallId,
    };
  }

  getReferringPersonId() {
    const { referrerType, referralContactId, referrerPersonId, prospectId } = this.state;
    switch (referrerType) {
      case 'Relative':
      case 'Acquaintance':
        return referrerPersonId;
      case 'Self':
        return prospectId;
      default:
        return referralContactId;
    }
  }

  computeRelationship() {
    const { referrerType, relationship, acquaintanceship } = this.state;
    return referrerType === 'Relative' ? relationship : acquaintanceship;
  }

  createCaseRecord() {
    const uri = '/api/case';
    const apiStruct = this.buildCaseApiStruct();
    const request = createStdRequest(uri, 'post', apiStruct);
    apiFetch(this, request, () => this.processSubmissionOperationsQueue());
  }

  farFutureDate = DateTime.local()
    .startOf('day')
    .plus(Duration.fromObject({ years: 5 }));

  buildCaseApiStruct() {
    const {
      prospectId,
      intakeBy,
      primaryClinician,
      facility,
      firstContactDate,
      expectedAdmissionDate,
      leadTemperature,
      salesStage,
      facilityName,
      prospectOwnerId,
    } = this.state;
    const caseId = 0;
    const creationDate = firstContactDate ? firstContactDate.toISO() : DateTime.local().startOf('day').toISO();
    const clientId = prospectId;
    const firmId = DA.getFirmId();
    const substanceNameStarts = [];
    const referralId = this.referralId;
    const admissionDate = expectedAdmissionDate.toISO();
    const admissionOwnerId = intakeBy || 0;
    const primaryClinicianId = primaryClinician || 0;
    const facilityId = facility || 0;
    const insuranceInfoId = 0;
    const treatmentProgramIds = [];
    const dischargeDate = this.farFutureDate.toISO();
    const extensions = [];
    const syncEmrFacility = facilityName;
    return {
      caseId,
      creationDate,
      clientId,
      firmId,
      substanceNameStarts,
      referralId,
      admissionDate,
      admissionOwnerId,
      primaryClinicianId,
      facilityId,
      insuranceInfoId,
      treatmentProgramIds,
      leadTemperature,
      salesStage,
      dischargeDate,
      extensions,
      syncEmrFacility,
      ownerId: prospectOwnerId,
    };
  }

  createNoteRecord() {
    const uri = '/api/note';
    const apiStruct = this.buildNoteApiStruct();
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = () => {
      this.props.clearTaskAccomplishmentInfo();
      // Don't need to install it in Redux, since the redirect to Case Page will fetch it.
      this.processSubmissionOperationsQueue();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildNoteApiStruct() {
    /* expected:
        public int NoteId { get; set; }
        public string Date { get; set; }
        public string AuthorEmail { get; set; }
        public int AuthorId { get; set; }
        public int ViewingFirmId { get; set; }
        public string Content { get; set; }
        public int SubjectId { get; set; }
     */
    const { prospectId, firstContactDate } = this.state;

    const authorId = this.state.primingInquiryId ? 0 /* probably not a User*/ : this.props.userInfo.personId;

    const noteId = 0;
    const viewingFirmId = DA.getFirmId();
    const content = this.state.note;
    const subjectId = prospectId;

    return {
      noteId,
      date: firstContactDate.toISO(),
      authorId,
      viewingFirmId,
      content,
      subjectId,
    };
  }

  saveVobFormInfo() {
    const { prospectId } = this.state;
    const uri = `/api/person/${prospectId}/insuranceInfo`;
    const vobFormInfos = DA.getVobFormInfosForPersonWithId(0); // becasue we didn't have a prospectID at the time
    vobFormInfos.forEach((vobFI) => {
      const apiStruct = this.buildInsuranceInfoApiStruct(prospectId, vobFI);
      const request = createStdRequest(uri, 'post', apiStruct);
      apiFetch(this, request);
    });
    this.processSubmissionOperationsQueue();
  }

  buildInsuranceInfoApiStruct(personId, vobFormInfo) {
    /* goal:
      public int InsuranceInfoId { get; set; }
      public int PersonId { get; set; }
      public int ViewingFirmId { get; set; }
      public string ProviderName { get; set; }
      public string TypeName { get; set; }
      public string CoverageLevelName { get; set; }
      public string GroupNumber { get; set; }
      public string PolicyNumber { get; set; }
      public string VobFormInfo { get; set; }
    */
    const viewingFirmId = DA.getFirmId();
    return {
      insuranceInfoId: 0,
      personId: personId,
      viewingFirmId: viewingFirmId,
      vobFormInfo: JSON.stringify(vobFormInfo),
    };
  }

  transitionToProspectStatus() {
    const uri = '/api/status';
    const apiStruct = this.buildStatusTransitionApiStruct();
    const request = createStdRequest(uri, 'post', apiStruct);
    apiFetch(this, request, () => this.processSubmissionOperationsQueue());
  }

  buildStatusTransitionApiStruct() {
    const firmId = DA.getFirmId();
    const clientId = this.state.prospectId;
    const date = DateTime.local();
    return {
      firmId,
      clientId,
      date: date.toISO(),
      newStatus: 'Prospect',
    };
  }

  convertInquiryNotes() {
    const primingInquiryId = this.state.primingInquiryId;
    if (primingInquiryId) {
      const firmId = DA.getFirmId();
      const prospectId = this.state.prospectId;
      const notes = DA.getNotesAboutInquiryWithId(primingInquiryId);
      const nNotes = notes.length;
      if (nNotes > 0) {
        let nNotesConverted = 0;
        notes.forEach((note) => {
          const noteId = note.noteId;
          const uri = `/api/note/${noteId}/convertFromInquiryToPerson`;
          const apiStruct = {
            firmId,
            noteId,
            inquiryId: primingInquiryId,
            personId: prospectId,
          };
          const request = createStdRequest(uri, 'PUT', apiStruct);
          const actionOnGoodResponse = (json) => {
            this.props.removeNoteOnInquiry(noteId);
            ReduxLoader.addOrUpdateNotesOnPersons([json]);
            nNotesConverted += 1;
            console.log(`nNotesConverted: ${nNotesConverted}`);
            if (nNotesConverted === nNotes) {
              this.processSubmissionOperationsQueue();
            }
          };
          apiFetch(this, request, actionOnGoodResponse);
        });
      } else {
        this.processSubmissionOperationsQueue();
      }
    } else {
      this.processSubmissionOperationsQueue();
    }
  }

  convertInquiryDocuments() {
    const primingInquiryId = this.state.primingInquiryId;
    if (primingInquiryId) {
      const firmId = DA.getFirmId();
      const prospectId = this.state.prospectId;
      const docs = DA.getDocumentsAboutInquiryWithId(primingInquiryId);
      const nDocs = docs.length;
      if (nDocs > 0) {
        let nDocsConverted = 0;
        docs.forEach((doc) => {
          const documentId = doc.storedDocumentId;
          const uri = `/api/document/${documentId}/convertFromInquiryToPerson`;
          const apiStruct = {
            firmId,
            documentId,
            inquiryId: primingInquiryId,
            personId: prospectId,
          };
          const request = createStdRequest(uri, 'PUT', apiStruct);
          const actionOnGoodResponse = (json) => {
            this.updateDocumentInReduxStore(json);
            nDocsConverted += 1;
            console.log(`nDocsConverted: ${nDocsConverted}`);
            if (nDocsConverted === nDocs) {
              this.processSubmissionOperationsQueue();
            }
          };
          apiFetch(this, request, actionOnGoodResponse);
        });
      } else {
        this.processSubmissionOperationsQueue();
      }
    } else {
      this.processSubmissionOperationsQueue();
    }
  }

  updateDocumentInReduxStore(doc) {
    this.props.updateDocument(doc);
  }

  deleteInquiry() {
    const primingInquiryId = this.state.primingInquiryId;
    if (primingInquiryId) {
      const uri = '/api/inquiry/' + primingInquiryId;
      const request = createStdRequest(uri, 'delete');
      const actionOnGoodResponse = () => {
        // remove inquiry from Redux, too.
        this.props.removeInquiry(primingInquiryId);
        this.setState({ primingInquiryId: 0 });
        this.processSubmissionOperationsQueue();
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      this.processSubmissionOperationsQueue();
    }
  }

  clearPageState() {
    this.referralId = 0;
    this.setState(this.blankPageState);
    this.processSubmissionOperationsQueue();
  }

  clearedObject(obj) {
    const newObj = {};
    Object.keys(obj).forEach((k) => (newObj[k] = false));
    return newObj;
  }

  clearReduxContactInfoState() {
    this.props.clearReferringContact();
    this.props.clearEmergencyContacts();
    this.processSubmissionOperationsQueue();
  }

  activateCasePrograms() {
    // TODO implement this
    this.processSubmissionOperationsQueue();
  }

  redirectToCasePageAfterSubmission() {
    this.props.setCaseIntendedClientId(this.state.prospectId);
    this.setState({ redirect: 'case' });
  }

  redirectHomeAfterSubmission() {
    this.setState({ redirect: '/homeDispatcher' });
  }

  refreshThisPage() {
    this.setState({ errorMessage: '' });
    this.appendSubmissionOperation('preselectSingleItemLists', {});
  }

  showVobForm(b) {
    this.setState({ shouldShowVobForm: b });
  }

  setProspectOwnerId(newValue) {
    this.setState({ prospectOwnerId: newValue });
  }

  forceShowingOfNewReferrerCompanyModal(referrerType) {
    if (isProprietorshipType(referrerType)) {
      this.setState({ referrerType, shouldShowProprietorshipModalStateVar: true });
    } else if (isPractitionerType(referrerType)) {
      this.setState({ referrerType, shouldShowPractitionerModalStateVar: true });
    } else {
      this.setState({ referrerType, shouldShowReferrerFirmModalStateVar: true });
    }
  }

  forceShowingOfNewContactModal(referrerType, firmId) {
    if (referrerType) {
      this.forceShowingOfNewOrgContactModal(referrerType, firmId);
    } else {
      this.forceShowingOfNewUnaffilContactModal(referrerType, firmId);
    }
  }

  forceShowingOfNewOrgContactModal(referrerType, firmId) {
    this.setState({ referrerType, referrerOrgId: firmId, shouldShowOrgContactModalStateVar: true });
  }

  forceShowingOfNewUnaffilContactModal(referrerType, firmId) {
    this.setState({ referrerType, referrerOrgId: firmId, shouldShowUnaffilContactModalStateVar: true });
  }

  forceShowingOfNewRelativeModal(relativeType) {
    this.setState({
      referrerType: 'Relative',
      relationship: relativeType,
      shouldShowPersonalReferrerModalStateVar: true,
    });
  }

  forceShowingOfNewAcquaintanceModal(acquaintanceType) {
    this.setState({
      referrerType: 'Acquaintance',
      acquaintanceship: acquaintanceType,
      shouldShowPersonalReferrerModalStateVar: true,
    });
  }

  setReferralCategory(newCategory) {
    this.setState({ chosenReferralCategory: newCategory, referralOptionalSpecies: '' });
  }

  setAsSelfReferral() {
    this.setState({ referrerType: 'Self' });
  }

  selectCategory(referrerType) {
    this.handleReferrerTypeChange(referrerType);
  }

  selectCompany(referrerType, referrerOrgId) {
    this.setState({ referrerType, referrerOrgId });
  }

  selectContact(referrerType, referrerOrgId, personId) {
    // if this is a Relative or Acquaintance we should clear referrerOrgId
    this.setState({ referrerType, referrerOrgId, referralContactId: personId });
  }

  mediaSpecificationString() {
    switch (this.state.chosenMediaOption) {
      case 'TV':
        return 'Channel';
      case 'Radio':
        return 'Station';
      case 'Print':
        return 'Newspaper or Magazine';
      case 'Internet':
        return 'Site';
      default:
        return 'Specify';
    }
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    const { classes } = this.props;
    const { intakers, clinicians } = this.props.firmInfo;
    const { emergencyContacts } = this.props.contactInfo;
    const possibleOwners = DA.getAllFirmUsers();
    const genderDropdownOptions = sortedDropdownItemLabels('Client Gender');
    const salesStageDropdownOptions = sortedDropdownItemLabels('Sales Stage');
    const commTypeDropdownOptions = sortedDropdownItemLabels('Referral Communication Type');
    const referralStatusDropdownOptions = sortedDropdownItemLabels('Prospect Status');
    const campaigns = DA.getAllCampaigns();
    const sortedCampaigns = campaigns.sort((f1, f2) => {
      const f1Name = f1.name;
      const f2Name = f2.name;
      if (f1Name < f2Name) {
        return -1;
      } else if (f2Name < f1Name) {
        return 1;
      } else {
        return 0;
      }
    });

    const campaignIsRequired = this.state.chosenReferralCategory === 'Marketing Event/Campaign';

    const {
      prospectEmail,
      prospectPhoneNumber1,
      prospectFirstName,
      prospectLastName,
      prospectBirthDate,
      prospectAddressLine1,
      prospectCity,
      prospectZip,
      prospectState,
    } = this.state;

    const phoneIsRequired = !prospectEmail;
    const emailIsRequired = !prospectPhoneNumber1;

    console.log(`state.communicationType: ${this.state.communicationType}`);

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <VobModal
          show={this.state.shouldShowVobForm}
          clientId={0}
          clientInfo={{
            clientFirstName: prospectFirstName,
            clientLastName: prospectLastName,
            clientBirthDate: prospectBirthDate,
            clientAddressLine1: prospectAddressLine1,
            clientCity: prospectCity,
            clientZip: prospectZip,
            clientState: prospectState,
          }}
          closeModalFunc={() => this.showVobForm(false)}
          shouldBeBlankFunc={(b) => this.setState({ vobFormShouldBeBlank: b })}
          shouldBeBlank={this.state.vobFormShouldBeBlank}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardHeader color="allevaOrange" icon>
                <div className={classes.summonVobFormButtonDiv}>
                  <CrmButton filled onClick={() => this.showVobForm(true)}>
                    Show VOB Info
                  </CrmButton>
                </div>
                <CardIcon className={classes.phoneIcon} color="allevaOrange">
                  <Phone />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>New Prospect Form</h4>
              </CardHeader>
              <CardBody>
                <div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={6}>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          required
                          label="Prospect First Name"
                          fullWidth
                          value={this.state.prospectFirstName}
                          onChange={(event) => this.setState({ prospectFirstName: event.target.value })}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          required
                          label="Prospect Last Name"
                          fullWidth
                          value={this.state.prospectLastName}
                          onChange={(event) => this.setState({ prospectLastName: event.target.value })}
                        />
                      </div>
                      <FormControl variant="standard" fullWidth style={{ marginTop: '-8px' }}>
                        <KDatePicker
                          label="Date of Birth"
                          value={this.state.prospectBirthDate}
                          onChange={(newDate) => this.handleDateOfBirthChange(newDate)}
                        />
                      </FormControl>
                      <div style={{ marginTop: '12px' }}>
                        <KSelect
                          id="simple-select"
                          name="prospectGender"
                          label="Gender"
                          fullWidth={true}
                          disabledOption="Choose Gender"
                          liveOptions={genderDropdownOptions}
                          value={this.state.prospectGender}
                          onChange={(event) => this.handleSelectChange(event)}
                        />
                      </div>
                      <div style={{ marginTop: '18px' }}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <PhoneInput
                              required={phoneIsRequired}
                              label={phoneIsRequired ? 'Phone 1 *' : 'Phone 1'}
                              possibleRoles={this.possibleProspectPhoneRoles}
                              actualRoles={this.state.prospectPhoneRoles1}
                              number={this.state.prospectPhoneNumber1}
                              onRolesChange={(rolesObj) => this.handleProspectPhoneRoles1Change(rolesObj)}
                              onNumberChange={(newNumber) => this.handleProspectPhoneNumber1Change(newNumber)}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <PhoneInput
                              label="Phone 2"
                              possibleRoles={this.possibleProspectPhoneRoles}
                              actualRoles={this.state.prospectPhoneRoles2}
                              number={this.state.prospectPhoneNumber2}
                              onRolesChange={(rolesObj) => this.handleProspectPhoneRoles2Change(rolesObj)}
                              onNumberChange={(newNumber) => this.handleProspectPhoneNumber2Change(newNumber)}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Address Line 1"
                          fullWidth
                          value={this.state.prospectAddressLine1}
                          onChange={(event) => this.setState({ prospectAddressLine1: event.target.value })}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Address Line 2"
                          fullWidth
                          value={this.state.prospectAddressLine2}
                          onChange={(event) => this.setState({ prospectAddressLine2: event.target.value })}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="City"
                          fullWidth
                          value={this.state.prospectCity}
                          onChange={(event) => this.setState({ prospectCity: event.target.value })}
                        />
                      </div>
                      <Grid container spacing={2}>
                        <Grid item className={classes.stateSelectItem} xs={8}>
                          <StateSelector
                            id="prospect-state"
                            value={this.state.prospectState}
                            onChange={(newValue) => this.handleStateChange(newValue)}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <CrmTextField
                            label="Zip Code"
                            fullWidth
                            value={this.state.prospectZip}
                            onChange={(event) => this.setState({ prospectZip: event.target.value })}
                          />
                        </Grid>
                      </Grid>
                      <Grid container style={{ marginTop: '-8px', marginLeft: '2px' }}>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            required={emailIsRequired}
                            label="Prospect Email"
                            fullWidth
                            value={this.state.prospectEmail}
                            onChange={(event) => this.setState({ prospectEmail: event.target.value })}
                          />
                        </div>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Showable show={emergencyContacts && emergencyContacts.length > 0}>
                        <h4>Emergency Contacts</h4>
                        {emergencyContacts.map((c, i) => (
                          <EmergencyContactBox
                            key={c.name}
                            index={i}
                            contact={c}
                            remove={this.props.removeEmergencyContact}
                          />
                        ))}
                      </Showable>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="standard" fullWidth>
                        <KDatePicker
                          label="Expected Admission Date"
                          value={this.state.expectedAdmissionDate}
                          onChange={(newDate) => this.handleAdmissionDateChange(newDate)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item className={classes.salesStageItem} xs={12} sm={6}>
                      <KSelect
                        id="sales-stage"
                        name="salesStage"
                        label="Sales Stage"
                        fullWidth={true}
                        disabledOption="Choose Stage"
                        liveOptions={salesStageDropdownOptions}
                        value={this.state.salesStage}
                        onChange={(event) => this.handleSelectChange(event)}
                      />
                    </Grid>
                  </Grid>
                  <hr className={classes.horizDivider} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <div>
                        <KDatePicker
                          label="First Contact Date"
                          value={this.state.firstContactDate}
                          onChange={(newDate) => this.handleFirstContactDateChange(newDate)}
                        />
                      </div>
                    </Grid>
                    <Grid className={classes.commTypeGridItem} item xs={12} sm={6} md={3}>
                      <div style={{ marginTop: '16px' }}>
                        <KSelect
                          required
                          id="communication-type"
                          name="communicationType"
                          label="Communication Type *"
                          fullWidth={true}
                          disabledOption="Choose Type"
                          liveOptions={commTypeDropdownOptions}
                          value={this.state.communicationType}
                          onChange={(event) => this.handleSelectChange(event)}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} />
                  </Grid>
                  <ReferralSelectionHierarchy
                    required
                    searchableContactData={this.searchableContactData}
                    chosenReferralCategory={this.state.chosenReferralCategory}
                    companyType={this.state.referrerType}
                    companyId={this.state.companyId}
                    contactId={this.state.referralContactId}
                    chosenWordOfMouthOption={this.state.chosenWordOfMouthOption}
                    chosenSocialMediaOption={this.state.chosenSocialMediaOption}
                    chosenMediaOption={this.state.chosenMediaOption}
                    referralOptionalSpecies={this.state.referralOptionalSpecies}
                    onReferralCategoryChange={(referralCategory) => this.setReferralCategory(referralCategory)}
                    onCompanyTypeChange={(referrerType) => this.selectCategory(referrerType)}
                    onCompanyChange={(referrerType, referrerOrgId) => this.selectCompany(referrerType, referrerOrgId)}
                    onContactChange={(referrerType, referrerOrgId, personId) =>
                      this.selectContact(referrerType, referrerOrgId, personId)
                    }
                    onWordOfMouthOptionChange={(newChoice) => this.setState({ chosenWordOfMouthOption: newChoice })}
                    onSocialMediaOptionChange={(newChoice) => this.setState({ chosenSocialMediaOption: newChoice })}
                    onMediaOptionChange={(newChoice) => this.setState({ chosenMediaOption: newChoice })}
                    onOptionalSpeciesChange={(newValue) => this.setState({ referralOptionalSpecies: newValue })}
                    onCreateNewCompany={(referrerType) => this.forceShowingOfNewReferrerCompanyModal(referrerType)}
                    onCreateNewContact={(referrerType, firmId) =>
                      this.forceShowingOfNewContactModal(referrerType, firmId)
                    }
                  />
                  <Grid style={{ marginTop: '14px' }} container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <KSelect
                        id="lead-temperature"
                        name="leadTemperature"
                        label="Prospect Status"
                        fullWidth={true}
                        liveOptions={referralStatusDropdownOptions}
                        value={this.state.leadTemperature}
                        onChange={(event) => this.handleSelectChange(event)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <KSelect
                        required={campaignIsRequired}
                        id="campaign"
                        name="campaign"
                        label={campaignIsRequired ? 'Campaign *' : 'Campaign'}
                        fullWidth={true}
                        disabledOption='Select Campaign (or "None")'
                        liveOptions={['None'].concat(sortedCampaigns.map((c) => c.name))}
                        liveValues={[0].concat(sortedCampaigns.map((c) => c.campaignId))}
                        value={this.state.campaign}
                        onChange={(event) => this.handleSelectChange(event)}
                      />
                    </Grid>
                    <Grid item className={classes.emergencyContactButtonDiv} xs={12} md={3}>
                      <Showable show={this.shouldShowAddEmergencyContactButton()}>
                        <CrmButton filled onClick={() => this.handleAddEmergencyContactClick()}>
                          Add Emergency Contact
                        </CrmButton>
                      </Showable>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Showable show={intakers && intakers.length > 0}>
                        <KSelect
                          id="intake-by"
                          name="intakeBy"
                          label="Prospective Client Intake By"
                          fullWidth={true}
                          disabledOption="Select Agent"
                          liveOptions={intakers.map((f) => f.name)}
                          liveValues={intakers.map((f) => f.id)}
                          value={this.state.intakeBy}
                          onChange={(event) => this.handleSelectChange(event)}
                        />
                      </Showable>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Showable show={clinicians && clinicians.length > 0}>
                        <KSelect
                          id="primary-clinician"
                          name="primaryClinician"
                          label="Primary Clinician"
                          fullWidth={true}
                          disabledOption="Select Clinician"
                          liveOptions={clinicians.map((c) => c.name)}
                          liveValues={clinicians.map((c) => c.id)}
                          value={this.state.primaryClinician}
                          onChange={(event) => this.handleSelectChange(event)}
                        />
                      </Showable>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Showable show={this.state.configureToSyncWithEmr}>
                      <EmrFacilitiesDropdown
                        page={this}
                        value={this.state.facilityName}
                        onChange={(event) => this.setState({ facilityName: event.target.value })}
                      />
                    </Showable>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <CrmTextField
                      label="Note"
                      fullWidth
                      multiline
                      value={this.state.note}
                      onChange={(event) => this.setState({ note: event.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <KSelect
                      label="Owner"
                      fullWidth={true}
                      disabledOption="Select Owner"
                      liveOptions={possibleOwners.map((c) => c.name)}
                      liveValues={possibleOwners.map((c) => c.personId)}
                      value={this.state.prospectOwnerId}
                      onChange={(event) => this.setProspectOwnerId(event.target.value)}
                    />
                  </Grid>
                  <div className={classes.submitButtonDiv}>
                    <CrmButton filled disabled={this.state.disableSubmitButton} onClick={() => this.handleSubmit()}>
                      Submit
                    </CrmButton>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Grid>
        </Grid>

        {this.shouldShowProprietorshipModal() && (
          <IcfConsultancyModal
            proprietorshipType={this.state.referrerType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelProprietorshipModal()}
          />
        )}

        {this.shouldShowReferrerFirmModal() && (
          <IcfReferrerFirmModal
            firmType={this.state.referrerType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewReferrerFirmSuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelReferrerFirmModal()}
          />
        )}

        {this.shouldShowPractitionerModal() && (
          <IcfPractitionerModal
            firmType={this.state.referrerType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelPractitionerModal()}
          />
        )}

        {this.shouldShowOrgContactModal() && (
          <IcfOrgContactModal
            firmId={this.state.referrerOrgId}
            onSuccess={(contactId) => this.handleNewOrgContactSuccess(contactId)}
            onCancel={() => this.cancelContactModal()}
          />
        )}

        {this.shouldShowUnaffilContactModal() && (
          <IcfUnaffiliatedContactModal
            onSuccess={(contactId) => this.handleNewUnaffilContactSuccess(contactId)}
            onCancel={() => this.cancelContactModal()}
          />
        )}

        {this.shouldShowPersonalReferrerModal() && (
          <IcfPersonalReferrerModal
            relationship={this.state.relationship || this.state.acquaintanceship}
            onSuccess={(contactId, relationship) => this.handleNewPersonalReferrer(contactId, relationship)}
            closeModalFunc={() => this.cancelContactModal()}
          />
        )}

        {this.shouldShowEmergencyContactModal() && (
          <IcfEmergencyContactModal
            onSuccess={(modalStateValues) => this.displayEmergencyContactFromValues(modalStateValues)}
            closeModalFunc={() => this.cancelContactModal()}
          />
        )}
      </div>
    );
  }
}

const EmergencyContactBox = ({ contact, index, remove }) => {
  const { name, email, phoneNumbers } = contact;
  const portionForLabel = 3;
  return (
    <Grid container spacing={1} style={{ border: '1px #dddddd solid' }}>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={portionForLabel}>
            Name
          </Grid>
          <Grid item xs={12 - portionForLabel}>
            {name}
          </Grid>
        </Grid>
      </Grid>
      {phoneNumbers.map((pn, i) => (
        <Grid item key={i} xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={portionForLabel}>
              Phone
            </Grid>
            <Grid item xs={12 - portionForLabel}>
              {`${pn.number} (${pn.roles})`}
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Showable show={email}>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={portionForLabel}>
              Email
            </Grid>
            <Grid item xs={12 - portionForLabel}>
              {email}
            </Grid>
          </Grid>
        </Grid>
      </Showable>
      <Grid item xs={12}>
        <CrmButton filled size="small" onClick={() => remove(index)}>
          Remove
        </CrmButton>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  firmToShow: state.firms.idOfFirmToShow,
  firmInfo: state.firmInfo,
  emrInfo: state.emrInfo,
  firms: state.firms,
  contactInfo: state.contactInfo,
  primingInfo: state.intendedInfo.initialCallFormPrimingInfo,
});

const actionProducers = {
  clearReferringContact,
  updateFirm,
  addEmergencyContact,
  removeEmergencyContact,
  clearEmergencyContacts,
  addClientToClientsInfo,
  setCaseIntendedClientId,
  removeNoteOnInquiry,
  updateDocument,
  removeInquiry,
  clearInitialCallFormPrimingInfo,
  clearTopReferrersInfo,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
  addEmrClient,
};

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(NewProspectPage));
